import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  XMarkIcon,
  MicrophoneIcon,
  StopIcon,
  PaperAirplaneIcon,
  SparklesIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import useStore from '../store/useStore';
import { aiApi } from '../services/api';
import { pmAssistantApi } from '../pm/services/pmApi';
import { usePageData } from '../pm/contexts/PageDataContext';

// Collapsible code block component
function CollapsibleCodeBlock({ code, title = "View Optimization Code" }) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="mt-3 border border-galaxy-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-3 py-2 bg-galaxy-800/50 hover:bg-galaxy-800 transition-colors"
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDownIcon className="w-4 h-4 text-galaxy-400" />
          ) : (
            <ChevronRightIcon className="w-4 h-4 text-galaxy-400" />
          )}
          <span className="text-xs font-medium text-galaxy-300">{title}</span>
        </div>
        <span className="text-xs text-galaxy-500">Python</span>
      </button>
      {isExpanded && (
        <pre className="p-3 bg-galaxy-950/50 overflow-x-auto">
          <code className="text-xs text-galaxy-200 font-mono">{code}</code>
        </pre>
      )}
    </div>
  );
}
// Map routes to page context type
const getPageContextType = (pathname) => {
  if (pathname.startsWith('/pm/real-asset/')) return 'real-asset-detail';
  if (pathname.startsWith('/workbench')) return 'workbench';
  if (pathname.startsWith('/jobs')) return 'jobs';
  if (pathname.startsWith('/customer-portal')) return 'customer-portal';
  if (pathname.startsWith('/asset/')) return 'fleet';
  if (pathname.startsWith('/fleet')) return 'fleet';
  return 'generic';
};

// Get context label for UI
const getContextLabel = (contextType, pageData) => {
  if (contextType === 'real-asset-detail' && pageData?.assetId) {
    return `PM Assistant (${pageData.assetId})`;
  }
  if (contextType === 'workbench') {
    const workbenchContext = pageData?.workbenchContext;
    if (workbenchContext?.activeTab === 'requirements') {
      return 'Requirements AI';
    }
    return 'Workbench AI';
  }
  if (contextType === 'fleet') return 'Fleet Assistant';
  if (contextType === 'jobs') return 'Jobs Assistant';
  if (contextType === 'customer-portal') return 'Customer Assistant';
  return 'Horizon Assistant';
};

function AiAssistant() {
  const { aiAssistantOpen, toggleAiAssistant, pendingAssistantMessage, clearPendingMessage, assets } = useStore();
  const location = useLocation();
  const { pageData } = usePageData();

  // Workbench context - will be passed via pageData when in workbench
  const workbenchContext = pageData?.workbenchContext || null;
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [width, setWidth] = useState(() => {
    const saved = localStorage.getItem('legal_aiAssistantWidth');
    return saved ? parseInt(saved, 10) : 384; // Default 384px (w-96)
  });
  const [isResizing, setIsResizing] = useState(false);
  const messagesEndRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Requirements context: input/output configuration
  const [selectedInputFiles, setSelectedInputFiles] = useState(new Set());
  const [outputDocumentName, setOutputDocumentName] = useState('Requirements Document');

  // Get current page context type
  const contextType = useMemo(() => getPageContextType(location.pathname), [location.pathname]);

  // Initialize selected input files when workbench files change
  useEffect(() => {
    if (contextType === 'workbench' && workbenchContext?.activeTab === 'requirements') {
      const selectedFileIds = workbenchContext.selectedRequirementFileIds || new Set();
      setSelectedInputFiles(new Set(selectedFileIds));
    }
  }, [contextType, workbenchContext?.activeTab, workbenchContext?.selectedRequirementFileIds]);

  // Sync output document name with workbench artifact title
  useEffect(() => {
    if (contextType === 'workbench' &&
        workbenchContext?.activeTab === 'requirements' &&
        workbenchContext?.setArtifactTitle) {
      workbenchContext.setArtifactTitle(outputDocumentName);
    }
  }, [contextType, workbenchContext?.activeTab, outputDocumentName, workbenchContext]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Save width to localStorage
  useEffect(() => {
    localStorage.setItem('legal_aiAssistantWidth', width.toString());
  }, [width]);

  // Handle pending messages from Document Extraction queries
  useEffect(() => {
    if (pendingAssistantMessage) {
      setInputText(pendingAssistantMessage);
      clearPendingMessage();
      // Auto-send the message
      setTimeout(() => {
        handleSendMessage(pendingAssistantMessage);
      }, 100);
    }
  }, [pendingAssistantMessage]);

  // Handle resize
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isResizing) {
        const newWidth = window.innerWidth - e.clientX;
        // Min width: 320px, Max width: 800px
        const clampedWidth = Math.max(320, Math.min(800, newWidth));
        setWidth(clampedWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const handleSendMessage = async (text) => {
    if (!text.trim()) return;

    const userMessage = { role: 'user', content: text };
    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsProcessing(true);

    try {
      // Workbench context: use kb-backend with streaming
      if (contextType === 'workbench' && workbenchContext) {
        // Requirements Gathering tab - use dedicated requirements endpoint
        if (workbenchContext.activeTab === 'requirements' && workbenchContext.uploadedRequirementsFiles?.length > 0) {
          // Use configured input files from config panel
          const fileIds = selectedInputFiles.size > 0
            ? Array.from(selectedInputFiles)
            : workbenchContext.uploadedRequirementsFiles.map(f => f.file_id);

          // Validation: Don't proceed if no files selected
          if (fileIds.length === 0) {
            setMessages(prev => [...prev, {
              role: 'assistant',
              content: 'Please select at least one input file for analysis.'
            }]);
            setIsProcessing(false);
            return;
          }

          // Build conversation history including the current artifact for context
          const conversationHistory = [];

          // Add recent chat messages for context
          messages.slice(-3).forEach(msg => {
            conversationHistory.push({
              role: msg.role,
              content: msg.content
            });
          });

          // Add current artifact to provide context for refinement
          if (workbenchContext.currentRequirementsDocument) {
            conversationHistory.push({
              role: 'assistant',
              content: `Current artifact:\n\n${workbenchContext.currentRequirementsDocument}`
            });
          }

          const response = await fetch('/api/requirements/analyze', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              file_ids: fileIds,
              query: text,
              conversation_history: conversationHistory
            })
          });

          if (!response.ok) throw new Error('Analysis failed');

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          let fullContent = '';

          // Add a status message to chat
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: 'Creating artifact in workspace...'
          }]);

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const data = JSON.parse(line.slice(6));

                if (data.type === 'text') {
                  fullContent += data.content;
                  // Update the workspace artifact in real-time
                  workbenchContext.setCurrentRequirementsDocument(fullContent);
                } else if (data.type === 'result') {
                  // Update chat with completion message
                  setMessages(prev => {
                    const updated = [...prev];
                    updated[updated.length - 1].content = 'Artifact updated in workspace. You can continue refining it or save it to your library.';
                    return updated;
                  });
                }
              }
            }
          }

          setIsProcessing(false);
          return; // Early return - don't use default chat endpoint
        }

        // Build message with context
        let message = text;

        // Add component context if on Space Optimization tab
        if (workbenchContext.activeTab === 'optimization' && workbenchContext.selectedComponents.length > 0) {
          message += `\n\nCurrent setup:\n- Container: ${workbenchContext.containerLength}m × ${workbenchContext.containerWidth}m\n- Components: ${workbenchContext.selectedComponents.length} selected`;
          message += `\n\nComponents:\n${workbenchContext.selectedComponents.map((c, i) => `#${i + 1}: ${c.dimensions[0]}m × ${c.dimensions[1]}m`).join('\n')}`;
        }

        // Add extraction result context if on Document Extraction tab and we have results
        if (workbenchContext.activeTab === 'extraction' && workbenchContext.extractionResult) {
          // Extract clean JSON for context
          const extractJson = (text) => {
            const jsonMatch = text.match(/```json\s*([\s\S]*?)\s*```/) || text.match(/```\s*([\s\S]*?)\s*```/);
            return jsonMatch ? jsonMatch[1].trim() : text;
          };
          const cleanJson = extractJson(workbenchContext.extractionResult);
          message += `\n\nPreviously extracted dimensional data (for reference):\n\`\`\`json\n${cleanJson}\n\`\`\``;
        }

        // Detect if this is an initial extraction query vs follow-up question
        const isInitialExtraction = workbenchContext.activeTab === 'extraction' &&
          (text.includes('Extract Complete Dimensional Data') ||
           text.includes('Analyze this SMT130 turbine plan view') ||
           text.includes('Generate Clearance Report') ||
           text.includes('Create Bill of Materials'));

        // Build request body
        const requestBody = {
          message: message,
          conversation_history: messages.map(m => ({ role: m.role, content: m.content })),
          use_available_documents: false  // No RAG - use Direct API for document extraction
        };

        // Only add current_document for INITIAL extraction queries (not follow-ups)
        if (isInitialExtraction && workbenchContext.currentDocument) {
          requestBody.current_document = workbenchContext.currentDocument;
        }

        // Add selected components and container dimensions for optimization
        if (workbenchContext.activeTab === 'optimization' && workbenchContext.selectedComponents.length > 0) {
          requestBody.selected_components = workbenchContext.selectedComponents;
          requestBody.container_length = workbenchContext.containerLength;
          requestBody.container_width = workbenchContext.containerWidth;
        }

        // Workbench uses KB backend with streaming
        const apiEndpoint = '/api/chat/stream';
        const apiBody = requestBody;

        const response = await fetch(apiEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(apiBody),
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error(`HTTP error ${response.status}`);
        }

        // Handle kb-backend (streaming response)
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let accumulatedText = '';
        let optimizationCode = null;

        // Add placeholder message
        const placeholderIndex = messages.length + 1;
        setMessages(prev => [...prev, { role: 'assistant', content: '', streaming: true }]);

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.trim() || !line.startsWith('data: ')) continue;

            try {
              const data = JSON.parse(line.slice(6));

              if (data.type === 'text') {
                accumulatedText += data.content;

                setMessages(prev => {
                  const newMessages = [...prev];
                  newMessages[placeholderIndex] = {
                    role: 'assistant',
                    content: isInitialExtraction ? 'Extracting data from PDF...' : accumulatedText,
                    streaming: true,
                    optimizationCode: optimizationCode
                  };
                  return newMessages;
                });
              } else if (data.type === 'tool_result') {
                // Capture optimization code
                if (data.result && data.result.optimization_code) {
                  optimizationCode = data.result.optimization_code;
                }

                // Update optimized layout in workbench context
                if (data.result && data.result.success && workbenchContext.setOptimizedLayout) {
                  workbenchContext.setOptimizedLayout({
                    trailer: {
                      length_m: workbenchContext.containerLength,
                      width_m: workbenchContext.containerWidth
                    },
                    components: data.result.components,
                    utilization: data.result.utilization
                  });
                }

                // If optimization failed, add error message to chat
                if (data.result && !data.result.success) {
                  accumulatedText += `\n\n❌ **Optimization Failed**\n\n${data.result.error}\n\nTry adjusting the constraints or container size.`;
                }

                // Update message with optimization code
                setMessages(prev => {
                  const newMessages = [...prev];
                  newMessages[placeholderIndex] = {
                    role: 'assistant',
                    content: accumulatedText,
                    streaming: true,
                    optimizationCode: optimizationCode
                  };
                  return newMessages;
                });
              }
            } catch (parseError) {
              console.warn('Failed to parse line:', line, parseError);
            }
          }
        }

        // Finalize message
        setMessages(prev => {
          const newMessages = [...prev];
          newMessages[placeholderIndex] = {
            role: 'assistant',
            content: isInitialExtraction
              ? '✅ Extraction complete! Switch to **JSON Results** view to see the extracted data.'
              : (accumulatedText || 'Optimization complete!'),
            optimizationCode: optimizationCode
          };
          return newMessages;
        });

        // Save extraction results to workbench context ONLY for initial extractions
        if (isInitialExtraction && accumulatedText) {
          // Extract only the JSON block from the response (filter out status messages)
          // Look for content starting from the first { or [ (JSON start)
          const jsonStartMatch = accumulatedText.match(/^[\s\S]*?(```json\s*[\s\S]*?\s*```|```\s*[\s\S]*?\s*```|[\{\[][\s\S]*)/);
          const cleanedResult = jsonStartMatch ? jsonStartMatch[1] : accumulatedText;

          if (workbenchContext.setExtractionResult) {
            workbenchContext.setExtractionResult(cleanedResult);
          }
        }

      } else if (contextType === 'fleet' || contextType === 'jobs' || contextType === 'customer-portal') {
        // Fleet/Jobs/Customer contexts: use base backend

        // Extract asset ID if on asset-specific page
        const assetIdMatch = location.pathname.match(/^\/asset\/([^/]+)/);
        const assetId = assetIdMatch ? assetIdMatch[1] : null;

        const apiBody = {
          message: text,
          context: messages.map(m => ({ role: m.role, content: m.content })),
          page: contextType === 'customer-portal' ? 'customer' : contextType,
          ...(assetId && { assetId })  // Include assetId if present
        };

        console.log('[Fleet AI] Sending request:', { contextType, assetId, page: apiBody.page, message: text });

        const response = await fetch('/api/ai/chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(apiBody),
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error(`HTTP error ${response.status}`);
        }

        const data = await response.json();
        const assistantMessage = {
          role: 'assistant',
          content: data.response || 'I apologize, but I encountered an error processing your request.'
        };
        setMessages(prev => [...prev, assistantMessage]);

      } else if (contextType === 'real-asset-detail') {
        // PM context: use PM backend
        const pageContext = {
          type: contextType,
          ...(pageData || {})
        };

        const conversationMessages = [...messages, userMessage].map(msg => ({
          role: msg.role,
          content: msg.content
        }));

        const response = await pmAssistantApi.chat(conversationMessages, pageContext);

        const assistantMessage = {
          role: 'assistant',
          content: response.data.response
        };
        setMessages(prev => [...prev, assistantMessage]);

      } else {
        // Generic fallback - use base backend with fleet context
        const apiBody = {
          message: text,
          context: messages.map(m => ({ role: m.role, content: m.content })),
          page: 'fleet'  // Default to fleet context for generic pages
        };

        const response = await fetch('/api/ai/chat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(apiBody),
          credentials: 'include'
        });

        if (!response.ok) {
          throw new Error(`HTTP error ${response.status}`);
        }

        const data = await response.json();
        const assistantMessage = {
          role: 'assistant',
          content: data.response || 'I apologize, but I encountered an error processing your request.'
        };
        setMessages(prev => [...prev, assistantMessage]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your request. Please try again.',
        error: true
      }]);
    } finally {
      setIsProcessing(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await processAudio(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const processAudio = async (audioBlob) => {
    setIsProcessing(true);
    try {
      // Still use Beusa backend for speech-to-text (OpenAI Whisper)
      const response = await aiApi.speechToText(audioBlob);
      const transcription = response.data.transcription;
      if (transcription) {
        handleSendMessage(transcription);
      }
    } catch (error) {
      console.error('Error processing audio:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Get context-appropriate suggestions
  const suggestions = useMemo(() => {
    if (contextType === 'workbench' && workbenchContext) {
      // Requirements Gathering tab - conditional on active case, uploaded files and artifact state
      if (workbenchContext.activeTab === 'requirements') {
        // If no active case, suggest going to My Cases
        if (!workbenchContext.activeCase) {
          return [
            'Go to My Cases tab to select or create a case first',
            'What types of legal documents can I analyze?'
          ];
        }

        // If we have case documents but no uploaded files
        const hasCaseDocuments = workbenchContext.activeCase?.documents?.length > 0;
        const hasUploadedFiles = workbenchContext.uploadedRequirementsFiles?.length > 0;

        if (hasCaseDocuments && !hasUploadedFiles && !workbenchContext.currentRequirementsDocument) {
          const caseName = workbenchContext.activeCase?.name || 'this case';
          return [
            `Analyze the case documents for ${caseName}`,
            'Identify key legal issues and applicable precedents',
            'Draft a motion to suppress based on Miranda violations',
            'Create a case strategy memo',
            'Research applicable case law from the Reference Library'
          ];
        }

        if (!hasUploadedFiles && !hasCaseDocuments) {
          return [
            'Upload documents to begin case analysis',
            'What file formats are supported?'
          ];
        }

        // If we have an artifact, show refinement suggestions
        if (workbenchContext.currentRequirementsDocument) {
          return [
            'Refine the legal analysis with more detail',
            'Add relevant case citations from the Reference Library',
            'Identify weaknesses in opposing arguments',
            'Draft recommended next steps',
            'Create a timeline of key events'
          ];
        }

        // Has files but no artifact yet - show initial creation suggestions
        return [
          'Create a comprehensive legal analysis',
          'Extract key facts and issues from the documents',
          'Identify applicable legal precedents',
          'Draft a case summary memo',
          'Analyze potential legal arguments'
        ];
      }

      // Document Extraction tab - conditional on whether extraction exists
      if (workbenchContext.activeTab === 'extraction') {
        if (workbenchContext.extractionResult) {
          // Follow-up questions when extraction exists
          return [
            'What are the dimensions of the bounding box around the turbine?',
            'What clearances are specified for maintenance access?',
            'List all components with their dimensions',
            'What are the cable entry points and their locations?'
          ];
        } else {
          // Initial extraction prompts when no extraction exists
          return [
            'Extract Complete Dimensional Data - 📐 Capture ALL dimensions and measurements into comprehensive JSON for spatial reasoning',
            'Generate Clearance Report - 📋 Analyze clearances, access requirements, and spatial conflicts',
            'Create Bill of Materials - 📦 Extract all components with designations and specifications'
          ];
        }
      }

      // Space Optimization tab - show optimization queries
      const numComponents = workbenchContext.selectedComponents.length;
      if (numComponents === 0) {
        return [
          'Add some components first to start optimizing',
          'How does the space optimization work?'
        ];
      }
      return [
        'Optimize the layout for compact packing',
        'Put component #1 on the left side',
        'Keep 2 meter clearance between all components',
        'Align components #1 and #2 horizontally'
      ];
    }
    if (contextType === 'real-asset-detail') {
      return [
        'What are the drivers of my current risk level?',
        'Show me recent sensor readings',
        'Why am I flagged for maintenance?'
      ];
    }
    if (contextType === 'fleet') {
      // Check if on asset-specific page
      const assetIdMatch = location.pathname.match(/^\/asset\/([^/]+)/);
      if (assetIdMatch) {
        const assetId = assetIdMatch[1];
        const asset = assets?.find(a => a.id === assetId);

        if (asset) {
          // Asset-specific questions based on status
          if (asset.status === 'npt') {
            return [
              'What is the best course of action?',
              'What caused this failure?',
              'How quickly can we get this back online?'
            ];
          } else if (asset.status === 'at_risk') {
            return [
              'What contributes to this prediction?',
              'When should I take action?',
              'What happens if I delay maintenance?'
            ];
          } else {
            // Healthy asset
            return [
              'What is the current status?',
              'Show recent performance metrics',
              'When is the next scheduled maintenance?'
            ];
          }
        }
      }

      // Fleet overview page (not asset-specific)
      return [
        'Show me all critical alerts',
        'Which assets need attention?',
        'What is the overall fleet health?'
      ];
    }
    if (contextType === 'jobs') {
      return [
        'Show pending maintenance jobs',
        'What jobs are in progress?',
        'Show completed jobs today'
      ];
    }
    if (contextType === 'customer-portal') {
      return [
        'Show current incidents',
        'What is our SLA compliance?',
        'Show uptime metrics'
      ];
    }
    return [
      'What can you help me with?',
      'Show fleet status',
      'Show recent alerts'
    ];
  }, [contextType, workbenchContext, workbenchContext?.extractionResult, workbenchContext?.selectedComponents?.length, aiAssistantOpen, assets, location.pathname]);

  const handleClearRequirementsChat = () => {
    // Clear all messages
    setMessages([]);
  };

  const handleInputFileToggle = (fileId, checked) => {
    setSelectedInputFiles(prev => {
      const updated = new Set(prev);
      if (checked) {
        updated.add(fileId);
      } else {
        updated.delete(fileId);
      }
      // Also update workbench context for consistency
      if (workbenchContext?.setSelectedRequirementFileIds) {
        workbenchContext.setSelectedRequirementFileIds(updated);
      }
      return updated;
    });
  };

  const handleOutputNameChange = (name) => {
    setOutputDocumentName(name);
    // Update artifact title in workbench context
    if (workbenchContext?.setArtifactTitle) {
      workbenchContext.setArtifactTitle(name);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    // Map extraction query labels to full queries
    const extractionQueryMap = {
      'Extract Complete Dimensional Data - 📐 Capture ALL dimensions and measurements into comprehensive JSON for spatial reasoning': `Analyze this SMT130 turbine plan view and capture ALL dimensions and measurements into a comprehensive JSON structure that enables spatial reasoning.

The goal is to create a complete dimensional database that can answer questions like:
- How far apart are specific elements?
- What is the length/width of each component?
- What are the clearance requirements between components?
- Where are components positioned relative to reference datums?

Please extract and structure:

1. **Overall Dimensions**: Total unit length, width, height
2. **Reference Datums**: Datum A, Datum B locations and what they reference
3. **Component Details**: For each labeled component (Turbine Air Inlet, Exhaust, Lube Oil Cooler, Enclosure Air Inlet/Exhaust, Demister, etc.):
   - Exact dimensions (length × width × height)
   - Position relative to datums (both imperial and metric)
   - Centerline positions
   - Mounting details
4. **Spatial Relationships**:
   - Distances between adjacent components
   - Minimum clearances specified
   - Access requirements
5. **All Measurements**: Include both imperial (inches/feet) and metric (mm/m) units
6. **Notes and Specifications**: Any callouts, notes, or special requirements

Output as a detailed JSON structure optimized for spatial reasoning and querying.`,

      'Generate Clearance Report - 📋 Analyze clearances, access requirements, and spatial conflicts': 'Analyze this turbine plan view and generate a clearance report. Identify minimum clearances between components, access requirements, and potential spatial conflicts. Format as a detailed report with sections.',

      'Create Bill of Materials - 📦 Extract all components with designations and specifications': 'Extract complete bill of materials from this plan view. List all identifiable components with designations, types, dimensions or part numbers. Output as a structured table or JSON.'
    };

    const fullQuery = extractionQueryMap[suggestion] || suggestion;
    handleSendMessage(fullQuery);
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 400 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 400 }}
      style={{ width: `${width}px` }}
      className="fixed right-0 top-0 h-full bg-galaxy-900/95 backdrop-blur-xl border-l border-galaxy-800/50 z-[1001] flex flex-col"
    >
      {/* Resize Handle */}
      <div
        onMouseDown={handleMouseDown}
        className={`absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize hover:bg-galaxy-500/50 transition-colors ${
          isResizing ? 'bg-galaxy-500' : ''
        }`}
        style={{ touchAction: 'none' }}
      />

      {/* Header */}
      <div className="p-4 border-b border-galaxy-800/50 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-galaxy-500 to-galaxy-700 flex items-center justify-center">
            <SparklesIcon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-white">
              {getContextLabel(contextType, pageData)}
            </h2>
            <p className="text-xs text-galaxy-400">AI-powered by Bedrock</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {contextType === 'workbench' && workbenchContext?.activeTab === 'requirements' && (
            <button
              onClick={handleClearRequirementsChat}
              className="px-2 py-1 text-xs text-red-400 hover:text-red-300 bg-red-900/20 hover:bg-red-900/30 border border-red-800/30 rounded transition-colors"
              title="Clear conversation history"
            >
              🗑️ Clear
            </button>
          )}
          <button
            onClick={toggleAiAssistant}
            className="p-2 text-galaxy-400 hover:text-white hover:bg-galaxy-800/50 rounded-lg transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Requirements Configuration Panel */}
      {contextType === 'workbench' && workbenchContext?.activeTab === 'requirements' &&
       workbenchContext.uploadedRequirementsFiles?.length > 0 && (
        <div className="requirements-config">
          <div className="config-section">
            <div className="config-header">
              <span className="config-label">📥 Input Files (Context)</span>
              <span className="config-count">
                {selectedInputFiles.size} selected
              </span>
            </div>
            <div className="input-files-list">
              {workbenchContext.uploadedRequirementsFiles.map(file => (
                <label key={file.file_id} className="file-checkbox-label">
                  <input
                    type="checkbox"
                    checked={selectedInputFiles.has(file.file_id)}
                    onChange={(e) => handleInputFileToggle(file.file_id, e.target.checked)}
                  />
                  <span className="file-icon">
                    {file.filename.endsWith('.pdf') ? '📄' :
                     file.filename.endsWith('.xlsx') ? '📊' : '📈'}
                  </span>
                  <span className="file-name">{file.filename}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="config-section">
            <label className="config-label">📤 Output Document</label>
            <input
              type="text"
              value={outputDocumentName}
              onChange={(e) => handleOutputNameChange(e.target.value)}
              placeholder="Requirements Document"
              className="output-name-input"
            />
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <SparklesIcon className="w-12 h-12 text-galaxy-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">
              {getContextLabel(contextType, pageData)}
            </h3>
            <p className="text-sm text-galaxy-400 mb-6">
              Ask questions and get insights powered by Claude Opus 4.5
            </p>

            {/* Context-aware Suggestions */}
            <div className="space-y-2">
              {suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="w-full text-left px-4 py-2 text-sm text-galaxy-300 bg-galaxy-800/30 hover:bg-galaxy-800/50 rounded-lg transition-colors"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((message, index) => (
          <div
            key={index}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] rounded-lg px-4 py-3 ${
                message.role === 'user'
                  ? 'bg-galaxy-600 text-white'
                  : message.error
                  ? 'bg-red-900/30 text-red-300 border border-red-700/50'
                  : 'bg-galaxy-800/50 text-galaxy-100'
              }`}
            >
              <div className="text-sm prose prose-sm prose-invert prose-strong:font-bold prose-strong:text-white prose-em:italic prose-em:text-galaxy-200 prose-a:text-blue-400 prose-a:underline hover:prose-a:text-blue-300 prose-blockquote:border-l-4 prose-blockquote:border-galaxy-600 prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-galaxy-300 prose-hr:border-galaxy-700 prose-p:my-1 prose-headings:my-2 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-code:bg-galaxy-700/50 prose-code:px-1 prose-code:rounded prose-code:text-galaxy-200 prose-pre:bg-galaxy-800 prose-pre:p-2 prose-table:text-sm prose-th:border prose-th:border-galaxy-600 prose-th:bg-galaxy-800/70 prose-th:px-3 prose-th:py-2 prose-td:border prose-td:border-galaxy-700 prose-td:px-3 prose-td:py-2 max-w-none">
                {message.role === 'assistant' ? (
                  <>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {message.content}
                    </ReactMarkdown>
                    {message.optimizationCode && (
                      <CollapsibleCodeBlock code={message.optimizationCode} />
                    )}
                  </>
                ) : (
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {message.content}
                  </ReactMarkdown>
                )}
              </div>
            </div>
          </div>
        ))}

        {isProcessing && (
          <div className="flex justify-start">
            <div className="bg-galaxy-800/50 rounded-lg px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-galaxy-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-galaxy-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-galaxy-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                <span className="text-sm text-galaxy-400">Processing...</span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="p-4 border-t border-galaxy-800/50">
        {/* Recording Indicator */}
        {isRecording && (
          <div className="mb-3 flex items-center justify-center gap-2 text-status-npt">
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="waveform-bar w-1 h-4 bg-status-npt rounded-full"
                  style={{ animationDelay: `${i * 0.1}s` }}
                />
              ))}
            </div>
            <span className="text-sm">Listening...</span>
          </div>
        )}

        <div className="flex items-center gap-2">
          {/* Voice Input Button */}
          <button
            onMouseDown={startRecording}
            onMouseUp={stopRecording}
            onMouseLeave={stopRecording}
            className={`p-3 rounded-full transition-colors ${
              isRecording
                ? 'bg-status-npt text-white'
                : 'bg-galaxy-800 text-galaxy-400 hover:text-white hover:bg-galaxy-700'
            }`}
          >
            {isRecording ? (
              <StopIcon className="w-5 h-5" />
            ) : (
              <MicrophoneIcon className="w-5 h-5" />
            )}
          </button>

          {/* Text Input */}
          <input
            type="text"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage(inputText)}
            placeholder="Type or speak..."
            className="flex-1 bg-galaxy-800/50 border border-galaxy-700/50 rounded-lg px-4 py-2.5 text-sm text-white placeholder-galaxy-500 focus:outline-none focus:border-galaxy-500"
          />

          {/* Send Button */}
          <button
            onClick={() => handleSendMessage(inputText)}
            disabled={!inputText.trim() || isProcessing}
            className="p-3 bg-galaxy-600 text-white rounded-full hover:bg-galaxy-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PaperAirplaneIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      <style>{`
        .requirements-config {
          background: rgba(30, 27, 75, 0.5);
          border-bottom: 1px solid rgba(99, 102, 241, 0.2);
          padding: 1rem;
        }

        .config-section {
          margin-bottom: 1rem;
        }

        .config-section:last-child {
          margin-bottom: 0;
        }

        .config-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;
        }

        .config-label {
          font-size: 0.75rem;
          font-weight: 600;
          color: rgba(99, 102, 241, 0.9);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .config-count {
          font-size: 0.75rem;
          color: rgba(224, 233, 255, 0.6);
        }

        .input-files-list {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          max-height: 150px;
          overflow-y: auto;
          padding: 0.5rem;
          background: rgba(15, 13, 38, 0.3);
          border-radius: 4px;
        }

        .file-checkbox-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem;
          cursor: pointer;
          border-radius: 4px;
          transition: background 0.2s;
        }

        .file-checkbox-label:hover {
          background: rgba(99, 102, 241, 0.1);
        }

        .file-checkbox-label input[type="checkbox"] {
          cursor: pointer;
        }

        .file-checkbox-label .file-icon {
          font-size: 1.25rem;
        }

        .file-checkbox-label .file-name {
          flex: 1;
          font-size: 0.875rem;
          color: rgba(224, 233, 255, 0.9);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .output-name-input {
          width: 100%;
          padding: 0.5rem;
          background: rgba(15, 13, 38, 0.5);
          border: 1px solid rgba(99, 102, 241, 0.3);
          border-radius: 4px;
          color: rgba(224, 233, 255, 0.9);
          font-size: 0.875rem;
        }

        .output-name-input:focus {
          outline: none;
          border-color: rgba(99, 102, 241, 0.6);
          background: rgba(15, 13, 38, 0.7);
        }
      `}</style>
    </motion.div>
  );
}

export default AiAssistant;
